<template>
  <div class="ui-pagination">
    <div class="ui-pagination__inner">
      <button
        v-if="(page !== 1 || !isMobile) && isLoaded"
        class="ui-pagination__button ui-pagination__button-nav transition--color-bg"
        @click="goBack">
        <arrow-icon class="ui-pagination__button-icon" />

        <span class="mobile--hidden">Previous Page</span>
        <span class="desktop--hidden">Previous Page</span>
      </button>

      <div class="ui-pagination__pages">
        <button
          v-for="button in buttons"
          :key="`ui-pagination-${button ? button : Math.random()}`"
          type="button"
          class="ui-pagination__button ui-pagination__button-page transition--color-bg"
          :class="{
            'ui-pagination__button--bg-active': button === page,
          }"
          @click="goToPage(button)">
          {{ button || '...' }}
        </button>
      </div>

      <button
        v-if="(page !== maxPage || !isMobile) && isLoaded"
        class="ui-pagination__button ui-pagination__button-nav ui-pagination__button-nav--next transition--color-bg"
        @click="goForward">
        <span class="mobile--hidden">Next Page</span>
        <span class="desktop--hidden">Next Page</span>

        <arrow-icon class="ui-pagination__button-icon ui-pagination__button-icon--reverse" />
      </button>
    </div>
  </div>
</template>

<script>
import ArrowIcon from 'Components/ui/Icons/ArrowIcon.vue';

const MAX_PAGE_BUTTONS = {
  desktop: 7,
  mobile: 5,
};

export default {
  name: 'UiPagination',

  components: { ArrowIcon },

  model: {
    prop: 'page',
    event: 'paginate',
  },

  props: {
    total: {
      type: Number,
      required: true,
    },

    page: {
      type: Number,
      required: true,
    },

    itemsPerPage: {
      type: Number,
      default: 20,
    },
  },

  data() {
    return {
      isLoaded: false,
    };
  },

  computed: {
    isMobile() {
      return this.$store.state.userDevice.innerWidth < 640;
    },

    maxPage() {
      return Math.ceil(this.total / this.itemsPerPage);
    },

    buttons() {
      const pageButtonsLength = MAX_PAGE_BUTTONS[this.isMobile ? 'mobile' : 'desktop'];

      if (this.maxPage < pageButtonsLength + 1) {
        return [...Array.from({ length: this.maxPage }, (_, i) => i + 1)];
      }

      if (this.page < pageButtonsLength - 2) {
        return [
          ...Array.from({ length: pageButtonsLength - 2 }, (_, i) => i + 1),
          null,
          this.maxPage,
        ];
      }

      if (this.page > (this.maxPage - (pageButtonsLength - 3))) {
        return [
          1,
          null,
          ...Array.from({ length: pageButtonsLength - 2 }, (_, i) => this.maxPage - ((pageButtonsLength - 3) - i)),
        ];
      }

      const isEven = Number(!(pageButtonsLength % 2));
      const prevPagesLength = Math.floor(pageButtonsLength / 2) - 2 - isEven;
      const nextPagesLength = Math.floor(pageButtonsLength / 2) - 2;
      const prevPagesArr = Array(prevPagesLength).fill(0).map((_, i) => this.page - (prevPagesLength - i));
      const nextPagesArr = Array(nextPagesLength).fill(0).map((_, i) => this.page + 1 + i);

      return [
        1,
        null,
        ...prevPagesArr,
        this.page,
        ...nextPagesArr,
        null,
        this.maxPage,
      ];
    },
  },

  mounted() {
    this.isLoaded = true;
  },

  methods: {
    goBack() {
      if (this.page > 1) {
        this.goToPage(this.page - 1);
      }
    },

    goForward() {
      if (this.page < this.maxPage) {
        this.goToPage(this.page + 1);
      }
    },

    goToPage(page) {
      if (page) {
        this.$emit('paginate', page);
      }
    },
  },
};
</script>

<style scoped lang="scss">
  .ui-pagination {
    overflow: hidden;
  }

  .ui-pagination__inner {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin: -16px calc(var(--gapX, 8px) * -1) 0;
  }

  .ui-pagination__pages,
  .ui-pagination__button-nav {
    margin: 16px var(--gapX, 8px) 0;
  }

  .ui-pagination__pages {
    display: grid;
    grid-gap: 16px;
    grid-auto-flow: column;
  }

  .ui-pagination__button {
    color: $color--basic-white;
    background-color: $color--basic-menu;
    border: none;
    padding: 1em;
    border-radius: 5px;
    font-weight: 500;
    display: grid;
    grid-gap: 10px;
    grid-auto-flow: column;
    align-items: center;
    justify-content: center;
    font-family: inherit;
  }

  .ui-pagination__button--bg-active {
    background-color: $color--basic-blue;
  }

  .ui-pagination__button-icon {
    width: 1.2em;
    height: 1.2em;
  }

  .ui-pagination__button-icon--reverse {
    transform: scaleX(-1);
  }

  @media only screen and (max-width: 374px) {
    .ui-pagination__button {
      padding: 0.8em;
      grid-gap: 7px;
    }
  }

  @media only screen and (max-width: 639.2px) {
    .mobile--hidden {
      display: none !important;
    }

    .ui-pagination__inner {
      --gapX: 8px;

      flex-wrap: wrap;
    }

    .ui-pagination__button-nav {
      flex: 1 0 40%;
    }

    .ui-pagination__button-nav--next {
      background-color: $color--basic-blue;
    }

    .ui-pagination__pages {
      order: -1;
      width: 100%;
    }

    .ui-pagination__button {
      font-size: 13px;
    }
  }

  @media only screen and #{$screen-more-or-640} {
    .desktop--hidden {
      display: none !important;
    }

    .ui-pagination__inner {
      --gapX: 16px;
    }

    .ui-pagination__button {
      font-size: 15px;
    }

    .ui-pagination__button-nav {
      padding: 1em 0.8em;
    }

    .ui-pagination__pages {
      margin-left: auto;
      margin-right: auto;
    }
  }

  @media #{$device--mouse} {
    .ui-pagination__button-page:not(.ui-pagination__button--bg-active):hover {
      background-color: $color--basic-dark-gray;
    }

    .ui-pagination__button-nav:hover {
      background-color: $color--basic-blue;
    }
  }
</style>
